import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { PencilSquareIcon } from '@heroicons/react/24/outline'

import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { OnboardingFormValues, OnboardingStepIndex } from '../types'

interface OnboardingReviewCardProps {
  values: OnboardingFormValues
  onEdit: (step: OnboardingStepIndex) => void
  /** Show the logo summary row. Disabled while logo onboarding is hidden from the UI. */
  showLogo?: boolean
}

const cardSx = {
  padding: '20px 24px',
  backgroundColor: INTERAX_COLORS.heritageBlue40,
  border: `1px solid ${INTERAX_COLORS.white10}`,
  borderRadius: '12px'
}

const sectionTitleSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: INTERAX_COLORS.white70,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5
}

const labelSx = {
  fontSize: 12,
  color: INTERAX_COLORS.thunderGrey,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  mb: 0.5
}

const valueSx = {
  fontSize: 15,
  color: INTERAX_COLORS.white,
  wordBreak: 'break-word' as const
}

const emptyValueSx = {
  fontSize: 14,
  color: INTERAX_COLORS.thunderGrey,
  fontStyle: 'italic' as const
}

const editButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  color: INTERAX_COLORS.sun,
  fontSize: 13,
  fontWeight: 500,
  '&:hover': { textDecoration: 'underline' }
}

/**
 * Read-only summary shown at the end of the wizard (final step). Each row has
 * an "Edit" affordance that jumps the wizard back to the relevant step.
 */
export const OnboardingReviewCard: React.FC<OnboardingReviewCardProps> = ({ values, onEdit, showLogo = true }) => {
  const { t } = useTranslation()

  const renderValue = (raw: string, emptyKey?: string) => {
    const v = (raw || '').trim()
    if (v) return <Typography sx={valueSx}>{v}</Typography>
    return <Typography sx={emptyValueSx}>{emptyKey ? t(emptyKey as never) : '—'}</Typography>
  }

  const addressLine = [values.street, values.city, values.country, values.postalCode]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(', ')

  return (
    <Box sx={cardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={sectionTitleSx}>{t('org.onboarding.reviewTitle')}</Typography>
      </Stack>

      <Stack spacing={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={labelSx}>{t('org.onboarding.nameLabel')}</Typography>
            <Box component="button" type="button" sx={editButtonSx} onClick={() => onEdit(0)}>
              <PencilSquareIcon style={{ width: 14, height: 14 }} />
              {t('org.onboarding.edit')}
            </Box>
          </Stack>
          {renderValue(values.name)}
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={labelSx}>{t('org.onboarding.addressLabel')}</Typography>
            <Box component="button" type="button" sx={editButtonSx} onClick={() => onEdit(1)}>
              <PencilSquareIcon style={{ width: 14, height: 14 }} />
              {t('org.onboarding.edit')}
            </Box>
          </Stack>
          {renderValue(addressLine)}
        </Box>

        {showLogo && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={labelSx}>{t('org.onboarding.logoLabel')}</Typography>
            </Stack>
            {values.logo?.trim() ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={values.logo}
                  alt=""
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '8px',
                    objectFit: 'contain',
                    backgroundColor: INTERAX_COLORS.offBlack,
                    border: `1px solid ${INTERAX_COLORS.white10}`
                  }}
                  onError={(e) => {
                    // Hide preview if URL fails — value still submitted as text.
                    ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                  }}
                />
                <Typography sx={{ ...valueSx, fontSize: 13, color: INTERAX_COLORS.white70 }}>{values.logo}</Typography>
              </Stack>
            ) : (
              <Typography sx={emptyValueSx}>{t('org.onboarding.logoEmpty')}</Typography>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  )
}
